import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  Check, ShieldCheck, Home, TrendingUp, HandCoins,
  FileText, Handshake, Sparkles, ArrowRight, Smartphone,
  Users, KeyRound, ScrollText, Wallet, Download,
} from "lucide-react";
import { MaskReveal, FadeUp, ClipReveal, GoldLineDraw } from "@/components/motion/Reveal";
import Marquee from "@/components/Marquee";
import FeaturedProjectsSection from "@/components/FeaturedProjectsSection";
import MortgageCalculator from "@/components/MortgageCalculator";
import PositioningHero from "@/components/PositioningHero";
import MavulaVsDubai from "@/components/MavulaVsDubai";
import FirstStepsBand from "@/components/FirstStepsBand";
import AlignmentBand from "@/components/AlignmentBand";
import InclusionBand from "@/components/InclusionBand";
import QuickQuestionsBand from "@/components/QuickQuestionsBand";
import ShareFamilyButton from "@/components/ShareFamilyButton";
import PartnersStrip from "@/components/PartnersStrip";
import ImpactBand from "@/components/ImpactBand";
import ImpactLiveCounter from "@/components/ImpactLiveCounter";
import ComparisonMatrix from "@/components/ComparisonMatrix";
import RiskWarning from "@/components/RiskWarning";
import Testimonials from "@/components/Testimonials";
import SisterSite from "@/components/SisterSite";
import FoundingMember from "@/components/FoundingMember";

const IMG = {
  hero_house: "https://images.pexels.com/photos/19344325/pexels-photo-19344325.jpeg",
  family: "https://images.unsplash.com/photo-1647616927583-1d44a79a38a5",
  apartment: "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267",
  modern_home: "https://images.pexels.com/photos/33050276/pexels-photo-33050276.jpeg",
  landscape: "https://images.unsplash.com/photo-1576485375217-d6a95e34d043",
};

const COUNTRIES = [
  { name: "South Africa", flag: "🇿🇦" },
  { name: "Zimbabwe", flag: "🇿🇼" },
  { name: "Zambia", flag: "🇿🇲" },
  { name: "Botswana", flag: "🇧🇼" },
  { name: "Namibia", flag: "🇳🇦" },
];

const MANIFESTO = [
  { n: "I", t: "Every citizen has a birthright", d: "To own land, to raise a family, to build in the soil that raised them. We open that door — regardless of where you live now." },
  { n: "II", t: "Fair terms. Fair word.", d: "A modest deposit to begin. A comfortable schedule to become the owner in full. Every figure disclosed in your individual project contract before you sign. Nothing hidden between the lines." },
  { n: "III", t: "Your papers abroad are not our business", d: "We only ask for your citizenship in the country where you plant your future — and we keep even that in confidence." },
  { n: "IV", t: "A pathway to settle. A promise kept.", d: "Title deed registered in your name. Independent lawyers. Government partnership. Dignity as standard equipment." },
];

// ---------------- reusable ----------------
function Eyebrow({ children, gold = false }) {
  return <div className={`mv-eyebrow ${gold ? "" : ""} mb-3`}><span style={{ color: "var(--mv-gold)" }}>{children}</span></div>;
}

function SectionTitle({ eyebrow, title, sub, center = false, italic = null }) {
  return (
    <div className={`${center ? "text-center max-w-3xl mx-auto" : "max-w-3xl"} mb-14`}>
      {eyebrow && <FadeUp><Eyebrow>{eyebrow}</Eyebrow></FadeUp>}
      <FadeUp delay={0.05}>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-[54px] leading-[1.02] mv-green-text">
          {title}
          {italic && <span className="block font-accent italic mt-1" style={{ color: "var(--mv-gold)" }}>{italic}</span>}
        </h2>
      </FadeUp>
      {sub && <FadeUp delay={0.1}><p className="mt-4 text-[15px] md:text-base leading-relaxed" style={{ color: "var(--mv-muted)" }}>{sub}</p></FadeUp>}
      <FadeUp delay={0.15}><GoldLineDraw className="mt-6 w-14" /></FadeUp>
    </div>
  );
}

// ---------------- HERO ----------------
function KineticHero() {
  const { t, i18n } = useTranslation();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const videoScale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const videoY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);
  // Force MaskReveal to re-mount when language changes so animations replay
  const lang = i18n.language;

  return (
    <section ref={heroRef} className="relative overflow-hidden" data-testid="hero-section">
      <div className="relative h-[100vh] min-h-[640px] max-h-[960px] w-full">
        <motion.div style={{ scale: videoScale, y: videoY }} className="absolute inset-0">
          <video
            data-testid="hero-video"
            autoPlay muted loop playsInline preload="metadata"
            poster={IMG.hero_house}
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src="https://videos.pexels.com/video-files/7578541/7578541-uhd_2560_1440_30fps.mp4" type="video/mp4" />
            <source src="https://videos.pexels.com/video-files/3773486/3773486-uhd_2560_1440_25fps.mp4" type="video/mp4" />
          </video>
        </motion.div>
        <div className="hero-fade" />

        <motion.div
          initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.7 }}
          className="absolute top-6 left-6 z-10 hidden md:flex items-center gap-3 text-white/85 text-[11px] tracking-[0.28em] uppercase"
        >
          <span className="w-6 h-px bg-white/60" />
          {t("hero.est")}
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.4, duration: 0.7 }}
          className="absolute top-6 right-6 z-10 hidden md:flex items-center gap-3 text-white/85 text-[11px] tracking-[0.28em] uppercase"
        >
          {t("hero.chapter")}
          <span className="w-6 h-px bg-white/60" />
        </motion.div>

        <motion.div style={{ y: contentY }} className="relative z-10 h-full max-w-7xl mx-auto px-4 md:px-8 flex flex-col justify-end pb-16 md:pb-24">
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.6 }}
            className="inline-flex self-start items-center gap-2 rounded-full px-3.5 py-1.5 mb-6 backdrop-blur-md"
            style={{ background: "rgba(10,59,40,0.6)", border: "1px solid rgba(212,175,55,0.55)" }}
          >
            <Sparkles size={14} style={{ color: "var(--mv-gold)" }} />
            <span className="text-[11px] uppercase tracking-[0.28em] text-white font-semibold">{t("hero.badge")}</span>
          </motion.div>

          <h1 key={lang} className="font-display text-white text-[clamp(2.5rem,7.4vw,7rem)] leading-[0.98] tracking-[-0.015em] max-w-5xl">
            <MaskReveal text={t("hero.brand")} as="span" delay={0.1} stagger={0.07} className="block" />
            <MaskReveal text={t("hero.tagline_line_1")} as="span" delay={0.4} stagger={0.06} italic className="block font-accent" />
            <MaskReveal text={t("hero.tagline_line_2")} as="span" delay={0.7} stagger={0.08} italic className="block font-accent" />
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.7 }}
            className="mt-8 max-w-2xl"
          >
            <p className="text-white/90 text-[15px] md:text-[17px] leading-relaxed">
              {t("hero.subtitle")}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.7 }}
            className="mt-8 flex flex-wrap gap-3"
          >
            <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}>
              <Link to="/waiting-list" className="btn-gold" data-testid="hero-open-account-btn">
                Join Waiting List <ArrowRight size={16} />
              </Link>
            </motion.div>
            <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}>
              <Link to="/how-it-works" className="btn-ghost !bg-white/10 !text-white !border-white/30 hover:!bg-white/20" data-testid="hero-how-it-works-btn">
                {t("cta.how_it_works")}
              </Link>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8, duration: 0.6 }}
            className="absolute bottom-6 right-6 md:right-8 hidden md:flex items-center gap-3 text-white/75 text-[11px] tracking-[0.24em] uppercase"
          >
            {t("hero.scroll")}
            <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity }} className="inline-block w-px h-8 bg-white/60" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

// ---------------- FOREVER YOURS TRUST BAND ----------------
function ForeverYoursBand() {
  return (
    <section className="py-16 md:py-20 bg-white border-y mv-hairline" data-testid="forever-yours-band">
      <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-7">
          <FadeUp>
            <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>§ Forever Yours</span></div>
          </FadeUp>
          <FadeUp delay={0.05}>
            <h2 className="font-display text-3xl md:text-5xl mv-green-text mt-3 leading-[1.02]">
              Your land, your name, <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>forever.</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <p className="mt-4 text-[15px] max-w-2xl" style={{ color: "var(--mv-muted)" }}>
              Once registered, we can never take it back. No fine print, no reversion clauses, no shared-ownership tricks.
              We do not own your home — you do.
            </p>
          </FadeUp>
          <FadeUp delay={0.15}>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/safety" className="btn-green" data-testid="home-safety-link">
                <ShieldCheck size={16}/> Read Our Protections
              </Link>
              <Link to="/stories" className="btn-ghost" data-testid="home-stories-link">
                Verified Success Wall
              </Link>
              <Link to="/roadmap" className="btn-ghost" data-testid="home-roadmap-link">
                Public Roadmap · Vote
              </Link>
            </div>
          </FadeUp>
        </div>
        <FadeUp className="lg:col-span-5" delay={0.1}>
          <div className="grid grid-cols-2 gap-3">
            {[
              { k: "Never", d: "Ask for your papers abroad" },
              { k: "Never", d: "Use your land as loan security" },
              { k: "Never", d: "Change terms without your consent" },
              { k: "Never", d: "Resell while you pay fairly" },
            ].map((c) => (
              <div key={c.d} className="mv-card p-4" style={{ background: "linear-gradient(180deg, #fff, rgba(233,60,60,0.03))" }}>
                <div className="text-[10.5px] uppercase tracking-[0.22em]" style={{ color: "#B33A3A" }}>{c.k}</div>
                <div className="font-display text-[14.5px] mv-green-text mt-1 leading-snug">{c.d}</div>
              </div>
            ))}
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

// ---------------- MARQUEE ----------------
function EditorialMarquee() {
  return (
    <section className="py-6 md:py-8 border-y mv-hairline bg-white text-neutral-900" data-testid="marquee-band">
      <Marquee
        items={[
          "Open Doors",
          "Fair Terms",
          "Home Ownership",
          "Pathway To Settlement",
          "Dignity",
          "Southern Africa",
          "Title Deed In Your Name",
          "No Papers Abroad",
        ]}
        speed={70}
      />
    </section>
  );
}

// ---------------- STATS BAND ----------------
function StatsBand() {
  return (
    <section className="py-16 md:py-24" data-testid="stats-band">
      <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-10">
        {[
          { k: "Low", l: "deposit to begin" },
          { k: "Fair", l: "developer instalment plans" },
          { k: "10 yrs", l: "typical repayment horizon" },
          { k: "Your name", l: "title deed on completion" },
        ].map((s, i) => (
          <FadeUp key={s.k} delay={i * 0.08}>
            <div>
              <div className="font-display text-[clamp(2.6rem,6vw,4.6rem)] leading-none mv-green-text tracking-tight">{s.k}</div>
              <div className="mt-3 text-[11.5px] uppercase tracking-[0.24em]" style={{ color: "var(--mv-muted)" }}>{s.l}</div>
              <GoldLineDraw className="mt-4 w-8" />
            </div>
          </FadeUp>
        ))}
      </div>
    </section>
  );
}

// ---------------- WHAT WE OFFER (Bento) ----------------
function WhatWeOffer() {
  return (
    <section className="py-20 md:py-28" data-testid="what-we-offer-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <SectionTitle
          eyebrow="§ What We Offer"
          title="Fair, transparent terms —"
          italic="designed for real families and real investors."
          sub="One clear structure. No hidden fees. Every figure disclosed in your individual project contract before you sign."
        />

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <FadeUp className="md:col-span-2 lg:col-span-2 lg:row-span-2">
            <motion.div
              whileHover={{ y: -4 }} transition={{ duration: 0.4 }}
              className="mv-card p-7 h-full flex flex-col justify-between"
              style={{ background: "var(--mv-green)", color: "#fff" }}
            >
              <div>
                <Eyebrow>Exclusive Benefit</Eyebrow>
                <h3 className="font-display text-3xl md:text-[38px] leading-tight">Pathway to Settlement</h3>
                <p className="mt-4 text-white/85 text-[14.5px] leading-relaxed">
                  We work closely with government leaders across the region to create special opportunities for investors
                  and home owners — relaxed residency rules and a secure future for you and your family.
                </p>
              </div>
              <ul className="mt-6 space-y-2.5 text-[14px]">
                <li className="flex gap-2"><Check size={16} style={{ color: "var(--mv-gold)" }} /> Relaxed residency rules</li>
                <li className="flex gap-2"><Check size={16} style={{ color: "var(--mv-gold)" }} /> Secure future for your family</li>
                <li className="flex gap-2"><Check size={16} style={{ color: "var(--mv-gold)" }} /> More news coming soon</li>
              </ul>
            </motion.div>
          </FadeUp>

          {[
            { icon: <Wallet size={22}/>, k: "Low deposit", label: "Modest entry", sub: "Exact figure set by each project" },
            { icon: <FileText size={22}/>, k: "10 years", label: "Typical repayment horizon", sub: "Comfortable schedule" },
            { icon: <TrendingUp size={22}/>, k: "Fair", label: "Developer instalment plan", sub: "Terms disclosed in your individual project contract" },
            { icon: <ShieldCheck size={22}/>, k: "No strict", label: "Local credit checks", sub: "Fair access, dignified process" },
          ].map((c, i) => (
            <FadeUp key={i} delay={i * 0.06} className="md:col-span-1 lg:col-span-2">
              <motion.div whileHover={{ y: -4 }} transition={{ duration: 0.35 }} className="mv-card p-6 h-full">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-4 mv-green-text" style={{ background: "rgba(212,175,55,0.16)" }}>
                  {c.icon}
                </div>
                <div className="font-display text-[34px] mv-green-text leading-none tracking-tight">{c.k}</div>
                <div className="mt-1 font-semibold text-[15px]">{c.label}</div>
                <div className="text-[13.5px]" style={{ color: "var(--mv-muted)" }}>{c.sub}</div>
              </motion.div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------- MANIFESTO CHAPTERS ----------------
function Manifesto() {
  return (
    <section className="py-24 md:py-32 relative overflow-hidden" data-testid="manifesto-section">
      <div className="absolute inset-0 pointer-events-none opacity-[0.35]" style={{
        background: "radial-gradient(600px 300px at 20% 0%, rgba(212,175,55,0.15), transparent 60%), radial-gradient(700px 300px at 80% 100%, rgba(10,59,40,0.10), transparent 60%)"
      }}/>
      <div className="max-w-7xl mx-auto px-4 md:px-8 relative">
        <div className="mb-16">
          <FadeUp><Eyebrow>§ The Manifesto</Eyebrow></FadeUp>
          <FadeUp delay={0.05}>
            <h2 className="font-display text-[clamp(2rem,5vw,4.4rem)] leading-[1.02] mv-green-text max-w-4xl">
              Four chapters, one open door. <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>Read as spoken.</span>
            </h2>
          </FadeUp>
        </div>

        <div className="space-y-16 md:space-y-24">
          {MANIFESTO.map((m, i) => {
            const align = i % 2 === 0;
            return (
              <div key={m.n} className={`grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-start ${align ? "" : "md:[direction:rtl] md:text-left"}`}>
                <FadeUp className={`md:col-span-3 ${align ? "" : "md:[direction:ltr]"}`}>
                  <div
                    className="font-display text-[clamp(6rem,15vw,13rem)] leading-none tracking-tight"
                    style={{ color: "rgba(10,59,40,0.10)", WebkitTextStroke: "1.5px rgba(10,59,40,0.55)" }}
                  >
                    {m.n}
                  </div>
                </FadeUp>
                <FadeUp className={`md:col-span-8 md:col-start-4 ${align ? "" : "md:[direction:ltr] md:col-start-1"}`} delay={0.1}>
                  <div className="max-w-2xl">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="w-10 h-px bg-[color:var(--mv-gold)]" />
                      <span className="text-[11px] tracking-[0.28em] uppercase" style={{ color: "var(--mv-muted)" }}>Chapter {m.n}</span>
                    </div>
                    <h3 className="font-display text-[clamp(1.6rem,3.2vw,3rem)] leading-[1.05] mv-green-text">{m.t}</h3>
                    <p className="mt-4 font-accent text-[clamp(1.1rem,1.4vw,1.35rem)] italic leading-relaxed" style={{ color: "var(--mv-text)" }}>
                      {m.d}
                    </p>
                  </div>
                </FadeUp>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ---------------- TRANSPARENCY ----------------
function TransparencyPanel() {
  return (
    <section className="py-20 md:py-28 bg-white border-y mv-hairline" data-testid="transparency-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <SectionTitle
            eyebrow="§ Full Transparency"
            title="Always know exactly"
            italic="where you stand."
            sub="Every payment, every receipt, every remaining pound — tracked and visible to you. Private, secure, only you can see your log."
          />
          <FadeUp>
            <ul className="space-y-3 text-[15px]" style={{ color: "var(--mv-text)" }}>
              {[
                "Full history of all your payments & receipts",
                "Progress updates on your property",
                "Remaining balance & future payments",
                "Download statements anytime",
                "Private & confidential — only you can see your log",
              ].map((x) => (
                <li key={x} className="flex gap-2.5">
                  <Check size={18} className="mt-0.5" style={{ color: "var(--mv-gold)" }} />{x}
                </li>
              ))}
            </ul>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="mt-8 flex gap-3">
              <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}>
                <Link to="/login" className="btn-green" data-testid="transparency-login-btn">Open Personal Log</Link>
              </motion.div>
              <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}>
                <Link to="/register-interest" className="btn-ghost">Register Interest</Link>
              </motion.div>
            </div>
          </FadeUp>
        </div>

        <FadeUp className="lg:col-span-7" delay={0.1}>
          <div className="mv-card overflow-hidden">
            <div className="mv-green-bg text-white px-6 py-5 flex items-center justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-[0.24em] text-white/70">Sample Breakdown</div>
                <div className="font-display text-2xl">£100,000 Family Home</div>
              </div>
              <span className="text-xs px-2.5 py-1 rounded-full" style={{ background: "var(--mv-gold)", color: "var(--mv-green)" }}>Illustrative</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4">
              {[
                { k: "Total Price", v: "£100,000" },
                { k: "Deposit", v: "£15,000" },
                { k: "Financed", v: "£85,000" },
                { k: "Rate", v: "Per contract" },
                { k: "Term", v: "10 years" },
                { k: "Monthly", v: "Per schedule" },
                { k: "Payment plan", v: "Developer-set" },
                { k: "Title deed", v: "Your name" },
              ].map((c) => (
                <div key={c.k} className="p-5 border mv-hairline">
                  <div className="text-[11.5px] uppercase tracking-[0.2em]" style={{ color: "var(--mv-muted)" }}>{c.k}</div>
                  <div className="font-display text-[26px] mv-green-text mt-1 tracking-tight">{c.v}</div>
                </div>
              ))}
            </div>
            <div className="p-5 bg-[var(--mv-bg)] text-[13.5px]" style={{ color: "var(--mv-muted)" }}>
              Figures are illustrative. Your official schedule is generated when you open an account and confirm citizenship.
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

// ---------------- SPOTLIGHT IMAGES (deliberate photography) ----------------
function SpotlightGallery() {
  return (
    <section className="py-20 md:py-28" data-testid="spotlight-gallery">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
          <div className="max-w-2xl">
            <FadeUp><Eyebrow>§ In Frame</Eyebrow></FadeUp>
            <FadeUp delay={0.05}>
              <h2 className="font-display text-3xl md:text-5xl mv-green-text leading-[1.05]">
                Real homes. Real families. <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>Real returns.</span>
              </h2>
            </FadeUp>
          </div>
          <FadeUp delay={0.1}>
            <div className="text-[12px] tracking-[0.24em] uppercase" style={{ color: "var(--mv-muted)" }}>
              Portfolio · Southern Africa · 2026
            </div>
          </FadeUp>
        </div>

        <div className="grid grid-cols-12 gap-4 md:gap-6">
          <ClipReveal className="col-span-12 md:col-span-8">
            <div className="relative overflow-hidden rounded-3xl aspect-[16/10]">
              <motion.img
                src={IMG.modern_home}
                alt="Modern home"
                loading="lazy"
                whileHover={{ scale: 1.04 }}
                transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                className="w-full h-full object-cover"
              />
              <div className="absolute left-4 bottom-4 px-3 py-1.5 rounded-full backdrop-blur-md text-white text-[11px] tracking-[0.24em] uppercase" style={{ background: "rgba(10,59,40,0.55)" }}>
                Frame 01 · Namibia
              </div>
            </div>
          </ClipReveal>

          <ClipReveal className="col-span-6 md:col-span-4" delay={0.15}>
            <div className="relative overflow-hidden rounded-3xl aspect-[3/4]">
              <img src={IMG.family} alt="Family" loading="lazy" className="w-full h-full object-cover" />
              <div className="absolute left-4 bottom-4 px-3 py-1.5 rounded-full backdrop-blur-md text-white text-[11px] tracking-[0.24em] uppercase" style={{ background: "rgba(10,59,40,0.55)" }}>
                Frame 02 · Home
              </div>
            </div>
          </ClipReveal>

          <ClipReveal className="col-span-6 md:col-span-5" delay={0.2}>
            <div className="relative overflow-hidden rounded-3xl aspect-[4/5]">
              <img src={IMG.apartment} alt="Interior" loading="lazy" className="w-full h-full object-cover" />
              <div className="absolute left-4 bottom-4 px-3 py-1.5 rounded-full backdrop-blur-md text-white text-[11px] tracking-[0.24em] uppercase" style={{ background: "rgba(10,59,40,0.55)" }}>
                Frame 03 · Interior
              </div>
            </div>
          </ClipReveal>

          <ClipReveal className="col-span-6 md:col-span-4" delay={0.25}>
            <div className="relative overflow-hidden rounded-3xl aspect-[4/5]">
              <img src={IMG.hero_house} alt="Home" loading="lazy" className="w-full h-full object-cover" />
              <div className="absolute left-4 bottom-4 px-3 py-1.5 rounded-full backdrop-blur-md text-white text-[11px] tracking-[0.24em] uppercase" style={{ background: "rgba(10,59,40,0.55)" }}>
                Frame 04 · Estate
              </div>
            </div>
          </ClipReveal>

          <ClipReveal className="col-span-12 md:col-span-3" delay={0.3}>
            <div className="relative overflow-hidden rounded-3xl aspect-[4/5] mv-green-bg p-6 text-white flex flex-col justify-between">
              <div className="font-display text-[42px] leading-none">05<span style={{color:"var(--mv-gold)"}}>.</span></div>
              <div>
                <div className="font-accent italic text-xl leading-tight" style={{ color: "var(--mv-gold)" }}>Every frame,</div>
                <div className="font-accent italic text-xl leading-tight" style={{ color: "var(--mv-gold)" }}>a promise.</div>
                <div className="mt-4 text-white/70 text-[11px] tracking-[0.24em] uppercase">Frame 05 · Manifesto</div>
              </div>
            </div>
          </ClipReveal>
        </div>
      </div>
    </section>
  );
}

// ---------------- COUNTRIES ----------------
function CountriesSection() {
  return (
    <section className="py-20 md:py-28" data-testid="countries-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <SectionTitle
          eyebrow="§ Who This Is For"
          title="All Southern Africans —"
          italic="wherever you live now."
          sub="We only verify citizenship in the country where you want to invest. We never ask about your papers abroad."
        />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {COUNTRIES.map((c, i) => (
            <FadeUp key={c.name} delay={i * 0.05}>
              <motion.div whileHover={{ y: -6 }} transition={{ duration: 0.35 }} className="mv-card p-5 flex flex-col items-start gap-2 h-full">
                <span className="text-3xl">{c.flag}</span>
                <div className="font-display text-xl mv-green-text">{c.name}</div>
                <div className="text-[12.5px]" style={{ color: "var(--mv-muted)" }}>Home ownership + Investor programs</div>
              </motion.div>
            </FadeUp>
          ))}
        </div>
        <FadeUp>
          <div className="mt-10 mv-card p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between" style={{ background: "linear-gradient(135deg, rgba(212,175,55,0.10), rgba(10,59,40,0.04))" }}>
            <div>
              <Eyebrow>Global Investors</Eyebrow>
              <h3 className="font-display text-2xl md:text-3xl mv-green-text mt-2">Investor programmes for certified investors — secured on titled property.</h3>
              <p className="mt-2 text-[14.5px]" style={{ color: "var(--mv-muted)" }}>Real social impact building homes and creating jobs. Financial terms are available only to visitors who self-certify as high-net-worth or sophisticated investors, in line with FCA rules.</p>
            </div>
            <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}>
              <Link to="/investors" className="btn-green" data-testid="countries-investors-btn">See Investor Terms <ArrowRight size={16}/></Link>
            </motion.div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

// ---------------- DIGNITY / INCLUSION ----------------
function DignitySection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <section ref={ref} className="py-24 md:py-32 bg-white border-y mv-hairline overflow-hidden" data-testid="dignity-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <ClipReveal className="lg:col-span-6">
          <div className="overflow-hidden rounded-3xl h-[520px]">
            <motion.img style={{ y: imgY }} src={IMG.family} alt="Family" className="w-full h-[130%] object-cover" loading="lazy" />
          </div>
        </ClipReveal>
        <div className="lg:col-span-6">
          <FadeUp><Eyebrow>§ Welcome To Every Citizen</Eyebrow></FadeUp>
          <FadeUp delay={0.05}>
            <h2 className="font-display text-3xl md:text-4xl lg:text-[54px] mv-green-text leading-[1.02]">
              You are part of us. You are welcome. <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>Build your future with dignity.</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <p className="mt-5 text-[15px] leading-relaxed" style={{ color: "var(--mv-muted)" }}>
              Every citizen has the right to own land, build a home, and invest in their own nation — regardless of your
              immigration status in the country where you live now. We do not ask about your papers abroad.
            </p>
          </FadeUp>
          <ul className="mt-6 space-y-3 text-[14.5px]">
            {[
              "We do not ask about your papers abroad — your status overseas does not change your right to invest and own property in your own homeland.",
              "We only confirm you are a citizen of the country where you want to invest — using your official national ID or passport from that nation.",
              "Fully confidential & private — your personal details are never shared.",
              "Safe, legal ways to send money into your personal MAVULA Investment Account.",
              "Every payment tracked & protected — full receipts and proof of every amount.",
              "Your money is only used for your property or investment — paid directly to projects in your home country.",
            ].map((t, i) => (
              <FadeUp key={t} delay={0.05 + i * 0.04}>
                <li className="flex gap-2.5"><Check size={18} className="mt-0.5" style={{ color: "var(--mv-gold)" }}/> {t}</li>
              </FadeUp>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

// ---------------- HOW IT WORKS ----------------
function HowItWorks() {
  const steps = [
    { icon: <Users size={22}/>, t: "Register", d: "Share your name, contact and which country you want to invest in." },
    { icon: <ScrollText size={22}/>, t: "Confirm Citizenship", d: "Show us your national ID or passport from that country only." },
    { icon: <Wallet size={22}/>, t: "Secure Your Account", d: "Pay your deposit or savings safely into your personal account." },
    { icon: <KeyRound size={22}/>, t: "Own Your Future", d: "We build or secure your property. Title deed in your name when fully paid." },
  ];
  return (
    <section id="how-it-works" className="py-24 md:py-32" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <SectionTitle eyebrow="§ How It Works" title="Four simple steps —" italic="from welcome to title deed." center />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {steps.map((s, i) => (
            <FadeUp key={i} delay={i * 0.08}>
              <motion.div whileHover={{ y: -6 }} transition={{ duration: 0.35 }} className="mv-card p-6 relative h-full">
                <div className="absolute -top-3 -left-3 w-9 h-9 rounded-full mv-green-bg text-white flex items-center justify-center font-display text-[15px]" style={{ boxShadow: "0 4px 12px rgba(10,59,40,0.25)" }}>{i+1}</div>
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-4 mv-green-text" style={{ background: "rgba(212,175,55,0.16)" }}>{s.icon}</div>
                <h3 className="font-display text-xl mv-green-text">{s.t}</h3>
                <p className="mt-2 text-[14px]" style={{ color: "var(--mv-muted)" }}>{s.d}</p>
              </motion.div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------- SECURITY ----------------
function SecuritySection() {
  return (
    <section className="py-24 md:py-32 bg-white border-y mv-hairline" data-testid="security-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <SectionTitle eyebrow="§ Your Security & Protection" title="Protection built in" italic="at every step." />
          <FadeUp>
            <p className="text-[15px] leading-relaxed" style={{ color: "var(--mv-muted)" }}>
              We use independent lawyers and banks, and we register the title deed in your name — so your ownership
              is not just promised, it is protected.
            </p>
          </FadeUp>
        </div>
        <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { i: <FileText size={20}/>, t: "Title deed in your name", d: "Registered once fully paid — fully yours." },
            { i: <Handshake size={20}/>, t: "Independent lawyers & banks", d: "Third parties safeguard your transaction." },
            { i: <ShieldCheck size={20}/>, t: "Fixed terms & fair contracts", d: "No hidden fees, no rate surprises." },
            { i: <Home size={20}/>, t: "Government-backed pathway", d: "Working with regional authorities for settlement rights." },
          ].map((c, i) => (
            <FadeUp key={i} delay={i * 0.06}>
              <motion.div whileHover={{ y: -3 }} transition={{ duration: 0.35 }} className="mv-card p-5 flex gap-4 items-start h-full">
                <div className="w-10 h-10 rounded-2xl flex items-center justify-center mv-green-text shrink-0" style={{ background: "rgba(212,175,55,0.16)" }}>{c.i}</div>
                <div>
                  <div className="font-semibold text-[15px] mv-green-text">{c.t}</div>
                  <div className="text-[13.5px] mt-1" style={{ color: "var(--mv-muted)" }}>{c.d}</div>
                </div>
              </motion.div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------- PERSONAL LOG PREVIEW ----------------
function PersonalLogPreview() {
  return (
    <section className="py-24 md:py-32" data-testid="personal-log-preview-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <SectionTitle
          eyebrow="§ Your Personal Log"
          title="A private dashboard —"
          italic="that tells you exactly where you stand."
          sub="See balance, monthly payments, property progress and receipts — anytime, from any device."
          center
        />
        <FadeUp>
          <div className="mv-card overflow-hidden">
            <div className="p-5 md:p-6 border-b mv-hairline flex flex-wrap gap-3 justify-between items-center bg-[var(--mv-bg)]">
              <div>
                <div className="text-[12px] tracking-[0.24em] uppercase" style={{ color: "var(--mv-muted)" }}>Owner</div>
                <div className="font-display text-xl mv-green-text">Thandiwe Moyo · Harare</div>
              </div>
              <div className="flex gap-2">
                <button className="btn-ghost !py-2"><Download size={14}/> Statement</button>
                <Link to="/login" className="btn-green !py-2" data-testid="preview-login-btn">Open My Log</Link>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 border-b mv-hairline">
              {[
                { k: "Total Price", v: "£100,000" },
                { k: "Paid so far", v: "£23,000" },
                { k: "Remaining", v: "£77,000" },
                { k: "Build Progress", v: "42%" },
              ].map(c => (
                <div key={c.k} className="p-5 md:p-6 border-r last:border-r-0 mv-hairline">
                  <div className="text-[11.5px] tracking-[0.2em] uppercase" style={{ color: "var(--mv-muted)" }}>{c.k}</div>
                  <div className="font-display text-3xl mv-green-text mt-1 tracking-tight">{c.v}</div>
                </div>
              ))}
            </div>
            <div className="p-5 md:p-6">
              <div className="text-[12px] uppercase tracking-[0.24em] mb-3" style={{ color: "var(--mv-muted)" }}>Recent Payments</div>
              <div className="divide-y mv-hairline">
                {[
                  { d: "15 Jan 2025", ref: "MV-2025-001", type: "Deposit", amt: "£15,000" },
                  { d: "05 Feb 2025", ref: "MV-2025-002", type: "Monthly instalment", amt: "£800" },
                  { d: "05 Mar 2025", ref: "MV-2025-003", type: "Monthly instalment", amt: "£800" },
                  { d: "05 Apr 2025", ref: "MV-2025-004", type: "Monthly instalment", amt: "£800" },
                ].map((p, i) => (
                  <div key={i} className="py-3 flex justify-between items-center text-[14px]">
                    <div className="flex gap-4">
                      <span className="w-24" style={{ color: "var(--mv-muted)" }}>{p.d}</span>
                      <span className="font-semibold mv-green-text">{p.type}</span>
                      <span className="hidden md:inline text-[13px]" style={{ color: "var(--mv-muted)" }}>{p.ref}</span>
                    </div>
                    <span className="font-semibold">{p.amt}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

// ---------------- MOBILE APP ----------------
function MobileApp() {
  return (
    <section className="py-24 md:py-32 bg-white border-y mv-hairline" data-testid="mobile-app-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <FadeUp><Eyebrow>§ Coming Soon</Eyebrow></FadeUp>
          <FadeUp delay={0.05}>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl mv-green-text leading-tight">
              Get the MAVULA <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>Mobile App</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <p className="mt-4 text-[15px]" style={{ color: "var(--mv-muted)" }}>
              Works on Android & iPhone. Low data, offline-friendly. Check your balance, schedule, upload documents,
              chat support and view your Personal Log — all in one place.
            </p>
          </FadeUp>
          <ul className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-2.5 text-[14px]">
            {["Check balance & payments","Full payment schedule","Upload documents","Live updates","24/7 support","View your personal log"].map((t, i) => (
              <FadeUp key={t} delay={0.03 * i}>
                <li className="flex gap-2"><Check size={16} style={{color:"var(--mv-gold)"}}/> {t}</li>
              </FadeUp>
            ))}
          </ul>
          <FadeUp delay={0.15}>
            <div className="mt-8 flex flex-wrap gap-3">
              <motion.button whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }} className="btn-green" data-testid="app-notify-google">
                <Smartphone size={16}/> Google Play — Notify Me
              </motion.button>
              <motion.button whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }} className="btn-ghost" data-testid="app-notify-apple">
                <Smartphone size={16}/> App Store — Notify Me
              </motion.button>
            </div>
            <div className="mt-4 text-[13px] font-accent italic" style={{ color: "var(--mv-muted)" }}>
              Launching soon — register your interest to be first to get it.
            </div>
          </FadeUp>
        </div>

        <FadeUp className="lg:col-span-6 order-1 lg:order-2" delay={0.1}>
          <div className="relative mx-auto w-[280px] md:w-[320px] aspect-[9/19] rounded-[42px] p-3" style={{ background: "var(--mv-green)", boxShadow: "0 30px 60px -20px rgba(10,59,40,0.35)" }}>
            <div className="w-full h-full rounded-[32px] overflow-hidden bg-white flex flex-col">
              <div className="h-8 flex items-center justify-center" style={{ background: "var(--mv-green)"}}>
                <div className="w-24 h-4 rounded-full bg-black/70" />
              </div>
              <div className="px-4 py-5 flex-1 flex flex-col gap-3 bg-[var(--mv-bg)]">
                <div className="text-[11px] uppercase tracking-[0.24em]" style={{color: "var(--mv-muted)"}}>MAVULA · Home</div>
                <div className="mv-card p-3">
                  <div className="text-[10px] uppercase tracking-[0.24em]" style={{color: "var(--mv-muted)"}}>Balance Remaining</div>
                  <div className="font-display text-2xl mv-green-text tracking-tight">£77,000</div>
                  <div className="text-[11px]" style={{color: "var(--mv-muted)"}}>Next: £800 · 05 May</div>
                </div>
                <div className="mv-card p-3">
                  <div className="text-[10px] uppercase tracking-[0.24em]" style={{color: "var(--mv-muted)"}}>Build Progress</div>
                  <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="h-2 mt-2 rounded-full bg-black/5 overflow-hidden">
                    <motion.div initial={{ width: 0 }} whileInView={{ width: "42%" }} viewport={{ once: true }} transition={{ duration: 1.2, delay: 0.2, ease: [0.22, 1, 0.36, 1] }} className="h-full" style={{ background: "var(--mv-gold)" }}/>
                  </motion.div>
                  <div className="text-[11px] mt-1 mv-green-text">42% · Foundation & Ground Floor</div>
                </div>
                <div className="mv-card p-3 flex-1">
                  <div className="text-[10px] uppercase tracking-[0.24em]" style={{color: "var(--mv-muted)"}}>Recent</div>
                  <div className="mt-2 text-[12px] space-y-1.5">
                    <div className="flex justify-between"><span>Feb 05</span><span className="mv-green-text font-semibold">£800</span></div>
                    <div className="flex justify-between"><span>Jan 15</span><span className="mv-green-text font-semibold">£15,000</span></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

// ---------------- INSTITUTIONAL ENQUIRIES BAND ----------------
function InvestorsBand() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  return (
    <section ref={ref} className="py-24 md:py-32 mv-green-bg text-white relative overflow-hidden" data-testid="investors-section">
      <motion.img style={{ y: bgY }} src={IMG.modern_home} alt="" className="absolute inset-0 w-full h-[130%] object-cover opacity-15" />
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <FadeUp><Eyebrow>Institutional Enquiries</Eyebrow></FadeUp>
          <FadeUp delay={0.05}>
            <h2 className="font-display text-3xl md:text-4xl lg:text-[54px] leading-[1.02]">
              Building homes and communities — <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>in partnership with governments and local developers.</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <p className="mt-4 text-white/85 text-[15px] leading-relaxed">
              We create jobs, build housing, and attract responsible capital to grow national economies. Institutional and eligibility-screened enquiries are handled privately by our UK compliance desk. Nothing on this website is a financial promotion or an offer of investment products.
            </p>
          </FadeUp>
          <FadeUp delay={0.15}>
            <div className="mt-8 flex flex-wrap gap-3">
              <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}>
                <Link to="/investors" className="btn-gold" data-testid="investors-register-btn">
                  Institutional Enquiry <ArrowRight size={16}/>
                </Link>
              </motion.div>
              <motion.div whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}>
                <Link to="/legal-status" className="btn-ghost !bg-white/10 !text-white !border-white/30 hover:!bg-white/20">Read Legal Status</Link>
              </motion.div>
            </div>
          </FadeUp>
        </div>
        <div className="lg:col-span-6 grid grid-cols-2 gap-4">
          {[
            { k: "UK", d: "Registered company" },
            { k: "Titled", d: "Real property, real communities" },
            { k: "Jobs", d: "Created locally" },
            { k: "Partners", d: "Licensed payment providers" },
          ].map((c, i) => (
            <FadeUp key={c.k} delay={i * 0.08}>
              <motion.div whileHover={{ y: -4 }} transition={{ duration: 0.35 }} className="p-5 rounded-2xl border border-white/15 h-full" style={{ background: "rgba(255,255,255,0.06)", backdropFilter: "blur(8px)" }}>
                <div className="font-display text-3xl md:text-[42px] tracking-tight" style={{ color: "var(--mv-gold)" }}>{c.k}</div>
                <div className="text-[14px] text-white/85 mt-1">{c.d}</div>
              </motion.div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------- CLARIFICATION ----------------
function Clarification() {
  return (
    <section className="py-16" data-testid="clarification-section">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <FadeUp>
          <div className="mv-card p-6 md:p-8 flex flex-col md:flex-row gap-6 items-start" style={{ background: "linear-gradient(180deg, #fff, rgba(212,175,55,0.06))" }}>
            <div className="w-12 h-12 shrink-0 rounded-2xl flex items-center justify-center" style={{ background: "rgba(10,59,40,0.08)" }}>
              <ShieldCheck className="mv-green-text" />
            </div>
            <div>
              <Eyebrow>Important Clarification</Eyebrow>
              <h3 className="font-display text-2xl mv-green-text mt-2">This is a structured purchase pathway — not an investment product.</h3>
              <p className="text-[14.5px] mt-2" style={{ color: "var(--mv-muted)" }}>
                Mavula Investment Ltd is <b>NOT a bank</b>, <b>NOT a money transfer / remittance service</b>, <b>NOT an FCA-authorised lender or investment firm</b>, and <b>does not offer investment products, yields or guaranteed returns</b>.
                All funds are processed through licensed payment partners and used only to buy, build, or secure property in your home country. No cash withdrawals. No sending money to other people. Read our full <Link to="/legal-status" className="underline mv-green-text">Legal Status</Link>.
              </p>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

// ---------------- PAGE ----------------
export default function HomePage() {
  return (
    <main>
      <KineticHero />
      <EditorialMarquee />
      <ForeverYoursBand />
      <FirstStepsBand />
      <PositioningHero />
      <MavulaVsDubai />
      <ImpactLiveCounter />
      <ImpactBand />
      <div className="max-w-6xl mx-auto px-4 md:px-8 pt-4"><RiskWarning compact /></div>
      <ComparisonMatrix />
      <AlignmentBand />
      <InclusionBand />
      <QuickQuestionsBand />
      <PartnersStrip />
      <StatsBand />
      <MortgageCalculator />
      <WhatWeOffer />
      <Manifesto />
      <TransparencyPanel />
      <FeaturedProjectsSection />
      <SpotlightGallery />
      <CountriesSection />
      <DignitySection />
      <HowItWorks />
      <SecuritySection />
      <PersonalLogPreview />
      <Testimonials />
      <MobileApp />
      <InvestorsBand />
      <SisterSite />
      <FoundingMember />
      <Clarification />
    </main>
  );
}
